import React, { useRef, useState } from 'react';
import { DSButton, DSIcon } from '../design-system/primitives';
import PinGolfHoleSheet from './PinGolfHoleSheet';
import { formatScoreAbbreviated } from '../utils/formatScore';
import './PinGolfHoleList.css';

// Single shared component for displaying/editing a PinGolf lineup - used by both
// the PinGolf League week card (pre-event lineup: machine + target + max balls,
// holes addable/removable) and the per-event Host Controls hole list (live holes:
// target + max balls only, no machine reassignment or removal once an event
// exists). Both read-only display and the editing flow live here once so a change
// to how a hole is shown/edited doesn't have to be made twice.
//
// Editing is one hole at a time in a sheet rather than every field of every hole
// inline. A nine-hole lineup put 27 controls plus a remove button on screen at
// phone width, each one a truncated select or a 64px number box sharing a row,
// with the save/cancel/randomize actions pushed below all of it. The row is now a
// summary that reads like the read-only list and opens the one hole being changed.
//
// holes: [{ key, holeNumber, machineId, name, targetScore, maxBalls, targetOverridden,
//   machineTargetScore }] - targetOverridden marks a hole with its own target, which a change
//   to the machine's target does not overwrite; it reads "Own target" on the row.
// editing: when true, each row opens an edit sheet for that hole.
// machines: optional array of { id, name } - when provided in edit mode, the
//   machine itself becomes editable; omit to keep the machine name fixed (e.g.
//   holes already tied to a live event).
// onChangeHole(key, field, value): required when editing. Writes go straight
//   through as the field changes, so the sheet has nothing to commit on close.
// onRemoveHole(key): optional - adds a remove action inside the sheet.
// onUseMachineTarget(key): optional - on a hole with its own target, adds a "Use machine
//   target" action inside the sheet that puts the hole back on its machine's target. It is
//   its own write, so any pending edit to that hole is dropped rather than committed.
// onCommit(key): optional - called with the edited hole's key when the sheet
//   closes and something was changed while it was open. A caller that saves per
//   hole rather than per lineup hooks its save here: one write when the hole is
//   done, not one per keystroke.
function PinGolfHoleList({ holes, editing = false, machines, onChangeHole, onRemoveHole, onUseMachineTarget, onCommit, disabled = false }) {
  const [editingKey, setEditingKey] = useState(null);
  const [dirty, setDirty] = useState(false);
  // The sheet does not move focus, so the row that opened it still has it on close.
  // For a tap that leaves a focus ring sitting on the row like a selection, so the
  // row is blurred - but only when it was opened by pointer, since a keyboard user
  // needs focus back where they left it.
  const triggerRef = useRef(null);
  const openedByPointer = useRef(false);

  // Looked up by key rather than held as an object, so the sheet renders the
  // current values as they are edited. A removed hole resolves to undefined,
  // which closes the sheet on its own.
  const editingHole = editing ? holes.find(hole => hole.key === editingKey) : null;

  const machineName = (hole) => {
    if (!machines) return hole.name;
    const machine = machines.find(entry => String(entry.id) === String(hole.machineId));
    return machine?.name || null;
  };

  const targetLabel = (targetScore) => (
    targetScore === null || targetScore === undefined || targetScore === ''
      ? 'unset'
      : formatScoreAbbreviated(targetScore)
  );

  const holeSummary = (hole) => `Target ${targetLabel(hole.targetScore)} - ${hole.maxBalls || 3} balls`;

  const changeHole = (key, field, value) => {
    setDirty(true);
    onChangeHole(key, field, value);
  };

  // The sheet is the unit of edit, so an untouched open-and-close saves nothing.
  const closeSheet = () => {
    setEditingKey(null);
    if (openedByPointer.current) triggerRef.current?.blur();
    if (dirty && onCommit) onCommit(editingKey);
    setDirty(false);
  };

  const useMachineTargetForEditingHole = () => {
    const key = editingHole.key;
    setEditingKey(null);
    setDirty(false);
    onUseMachineTarget(key);
  };

  const ownTargetMarker = (hole) => (
    hole.targetOverridden ? <span className="pgh-own-target"> - Own target</span> : null
  );

  const removeEditingHole = () => {
    const key = editingHole.key;
    setEditingKey(null);
    // The removal is its own write; a pending field edit on a hole about to
    // disappear has nothing left to commit.
    setDirty(false);
    onRemoveHole(key);
  };

  return (
    <>
      <ul className="pgh-list">
        {holes.map((hole) => (
          <li key={hole.key} className="pgh-item">
            {editing ? (
              <button
                type="button"
                className="pgh-edit-trigger"
                onPointerDown={() => { openedByPointer.current = true; }}
                onKeyDown={() => { openedByPointer.current = false; }}
                onClick={(event) => {
                  triggerRef.current = event.currentTarget;
                  setEditingKey(hole.key);
                }}
                disabled={disabled}
              >
                <span className="pgh-number">{hole.holeNumber}</span>
                {/* Name over summary rather than beside it: machine names run long enough
                    that sharing the row with "Target 5.0M - 3 balls" wrapped most of them
                    to three lines for no gain - the summary is a fixed short string. */}
                <span className="pgh-main">
                  <span className={`pgh-name${machineName(hole) ? '' : ' pgh-name-unset'}`}>
                    {machineName(hole) || 'Pick a machine'}
                  </span>
                  <span className="pgh-detail">{holeSummary(hole)}{ownTargetMarker(hole)}</span>
                </span>
                <DSIcon name="chevronRight" size="sm" />
              </button>
            ) : (
              <div className="pgh-row">
                <span className="pgh-number">{hole.holeNumber}</span>
                <span className="pgh-main">
                  <span className="pgh-name">{hole.name}</span>
                  <span className="pgh-detail">{holeSummary(hole)}{ownTargetMarker(hole)}</span>
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>

      {editingHole && (
        <PinGolfHoleSheet
          title={`Hole ${editingHole.holeNumber}`}
          hole={editingHole}
          machines={machines}
          disabled={disabled}
          onChange={(field, value) => changeHole(editingHole.key, field, value)}
          onClose={closeSheet}
          footer={(
            <>
              {onUseMachineTarget && editingHole.targetOverridden && (
                <DSButton
                  appearance="text"
                  tone="neutral"
                  onClick={useMachineTargetForEditingHole}
                  disabled={disabled}
                >
                  {editingHole.machineTargetScore !== null && editingHole.machineTargetScore !== undefined
                    ? `Use machine target (${formatScoreAbbreviated(editingHole.machineTargetScore)})`
                    : 'Use machine target'}
                </DSButton>
              )}
              {onRemoveHole && (
                <DSButton
                  appearance="text"
                  tone="danger"
                  onClick={removeEditingHole}
                  disabled={disabled}
                >
                  Remove hole
                </DSButton>
              )}
              <DSButton onClick={closeSheet}>Done</DSButton>
            </>
          )}
        />
      )}

    </>
  );
}

export default PinGolfHoleList;
